/**
 * Nettoie les paramètres d’URL / chaînes courtes (router).
 * Sans DOMPurify ici : le bundle initial évite ~15–20 Kio gzip ; le HTML riche reste assaini
 * avec DOMPurify dans BlogPostView et les vues concernées.
 */
export function sanitizeInput(input: string): string {
  if (!input) return ''
  return input.replace(/\0/g, '').replace(/<[^>]*>/g, '')
}

/**
 * Validate user input against common security threats
 */
export function validateInput(input: string): boolean {
  if (!input) return false

  // Liste des patterns dangereux
  const dangerousPatterns = [
    /[<>]/g,                    // Tags HTML
    /javascript:/gi,            // Protocole javascript:
    /data:/gi,                  // Protocole data:
    /vbscript:/gi,             // VBScript
    /on\w+=/gi,                // Event handlers
    /style=/gi,                // Style inline
    /\\/gi,                    // Caractères d'échappement
    /%[0-9A-F]{2}/gi,         // Encodage URL suspect
    /\.\./gi,                  // Directory traversal
    /exec\s*\(/gi,            // Commandes système
    /system\s*\(/gi,          // Commandes système
    /passw(or)?d/gi,          // Mots de passe
    /select.+from/gi,         // SQL injection
    /union.+select/gi,        // SQL injection
    /insert.+into/gi,         // SQL injection
    /drop.+table/gi,          // SQL injection
    /alert\s*\(/gi,           // JavaScript alert
    /confirm\s*\(/gi,         // JavaScript confirm
    /prompt\s*\(/gi,          // JavaScript prompt
    /document\./gi,           // Accès au DOM
    /window\./gi,             // Accès à window
    /\$\{.*\}/gi,            // Template injection
    /process\./gi,            // Node.js process
    /require\s*\(/gi,         // Node.js require
    /eval\s*\(/gi,           // JavaScript eval
    /function\s*\(/gi,       // JavaScript function
    /setTimeout\s*\(/gi,     // JavaScript setTimeout
    /setInterval\s*\(/gi,    // JavaScript setInterval
    /new\s+Function/gi,      // JavaScript new Function
    /\[\s*?\$\s*?\w+\s*?\]/gi // SQL injection
  ]

  // Vérifie si l'input contient des patterns dangereux
  return !dangerousPatterns.some(pattern => pattern.test(input))
}

/**
 * Encode HTML entities to prevent XSS
 */
export function encodeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Generate a nonce for CSP
 */
export function generateNonce(): string {
  const array = new Uint8Array(16)
  window.crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Validate file type and size
 */
export function validateFile(file: File, allowedTypes: string[], maxSize: number): boolean {
  // Vérifier le type MIME
  if (!allowedTypes.includes(file.type)) {
    console.error(`Type de fichier non autorisé: ${file.type}`)
    return false
  }

  // Vérifier la taille
  if (file.size > maxSize) {
    console.error(`Fichier trop volumineux: ${file.size} bytes`)
    return false
  }

  return true
}

/**
 * Validate URL against common security threats
 */
export function validateURL(url: string): boolean {
  try {
    const parsedURL = new URL(url)
    
    // Liste des protocoles autorisés
    const allowedProtocols = ['http:', 'https:']
    if (!allowedProtocols.includes(parsedURL.protocol)) {
      return false
    }

    // Vérifier les caractères dangereux dans l'URL
    const dangerousChars = /[<>"'%{}\\^`]/
    if (dangerousChars.test(url)) {
      return false
    }

    return true
  } catch {
    return false
  }
}

/**
 * Create secure headers for fetch requests
 */
export function createSecureHeaders(): HeadersInit {
  return {
    'Content-Security-Policy': "default-src 'self'",
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), microphone=(), camera=()'
  }
} 

/**
 * Désactiver le service worker s'il est enregistré
 */
export function disableServiceWorker(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (const registration of registrations) {
        console.log('🔄 Désactivation du service worker:', registration.scope)
        registration.unregister()
      }
    })
  }
}

/**
 * Note: La CSP est maintenant gérée uniquement côté serveur via les en-têtes HTTP
 * pour une meilleure sécurité et conformité avec les recommandations Lighthouse.
 * Cette fonction a été supprimée pour éviter les conflits avec la CSP serveur.
 */ 